/**
 * Aircraft geometry API client — the fast exact-Bezier VSP path.
 *
 * Calls the thread-api `/geometry/aircraft` route, which pulls OpenVSP's real
 * m_Surface control nets (machine-precision, no EGADS, no point-fit) and
 * tessellates them to glTF in ~100ms. Powers the V5 workbench "Aircraft" mode.
 */

import { apiClient, getAuthToken } from "./client";

export interface AircraftComponent {
  geom_id: string;
  name: string;
  patches: number;
  vertices: number;
  triangles: number;
}

export interface AircraftBuildResponse {
  bundle_key: string;
  model_url: string;     // glTF for the 3D viewer (full /api path)
  bezier_url: string;    // exact control-net model (compact, resolution-independent)
  metadata_url: string;
  components: AircraftComponent[];
  bbox: [[number, number, number], [number, number, number]];
  total_patches: number;
  total_triangles: number;
  cache_hit: boolean;
  build_ms: number;
}

export interface AircraftBuildRequest {
  model_key?: string;
  vsp3_path?: string;
  resolution?: number;
  force?: boolean;
  /** VSP parm id -> value, applied worker-side (SetParmVal + Update) before
   *  extraction. Omit/null for the stock geometry (unchanged cache key). */
  parm_overrides?: Record<string, number> | null;
}

/** Build (or fetch cached) exact aircraft geometry for a bundled model. */
export async function buildAircraft(req: AircraftBuildRequest): Promise<AircraftBuildResponse> {
  return apiClient<AircraftBuildResponse>("/geometry/aircraft/build", {
    method: "POST",
    body: req,
  });
}

// --- catalog ---------------------------------------------------------------------

/** One model the aircraft screen can build: a prebaked bundle or a fleet .vsp3. */
export interface AircraftCatalogModel {
  key: string;
  label: string;
  kind: "prebaked" | "fleet";
  vsp3_path?: string;
  designable?: boolean;
}

export interface AircraftCatalogResponse {
  models: AircraftCatalogModel[];
}

/** List buildable models: the prebaked bundles + the scanned VSP fleet dir. */
export async function fetchAircraftCatalog(): Promise<AircraftCatalogResponse> {
  return apiClient<AircraftCatalogResponse>("/geometry/aircraft/catalog");
}

// --- design parms ----------------------------------------------------------------

/** One design-relevant VSP parm (float/int only, server-filtered + capped). */
export interface AircraftParm {
  id: string;
  name: string;
  group: string;
  value: number;
  /** Limits straight from VSP - may be huge sentinels (|x| ~ 1e12); clamp UI-side. */
  lo: number;
  hi: number;
}

export interface AircraftGeomParms {
  id: string;
  name: string;
  type: string;
  parms: AircraftParm[];
}

export interface AircraftParmsResponse {
  geoms: AircraftGeomParms[];
}

/** Fetch the live-editable design parms for a model (needs the VSP engine +
 *  a real .vsp3 - prebaked-only models 4xx/503, callers hide the panel). */
export async function fetchAircraftParms(req: {
  modelKey?: string;
  vsp3Path?: string;
}): Promise<AircraftParmsResponse> {
  return apiClient<AircraftParmsResponse>("/geometry/aircraft/parms", {
    method: "POST",
    body: { model_key: req.modelKey ?? null, vsp3_path: req.vsp3Path ?? null },
  });
}

// --- designs (project-scoped editable aircraft) ------------------------------------

/** The geom types the design workbench can create (AddGeom worker-side). */
export type AircraftDesignGeomType = "WING" | "FUSELAGE" | "POD" | "STACK" | "BODYOFREVOLUTION";

/** One design edit, applied worker-side IN ORDER and appended to the design
 *  node's ops_log — the log is the design's provenance record. */
export type AircraftDesignOp =
  | { op: "add_geom"; type: AircraftDesignGeomType; parent?: string | null; name?: string | null }
  | { op: "delete_geom"; id: string }
  | { op: "rename_geom"; id: string; name: string }
  | { op: "set_parms"; values: Record<string, number> };

/** A design as listed: a project-owned .vsp3 plus its recorded edit history. */
export interface AircraftDesignSummary {
  design_id: string;
  name: string;
  /** Where the file came from: 'blank', a catalog model key, or a source design. */
  base: string;
  ops_count: number;
  updated_at?: string;
}

export interface AircraftDesignsResponse {
  designs: AircraftDesignSummary[];
}

export interface AircraftDesignCreated {
  design_id: string;
  name: string;
  base: string;
}

/** One geom as the worker describes it after applying ops (VSP geom id). */
export interface AircraftDesignGeom {
  id: string;
  name: string;
  type: string;
  parent: string | null;
}

/** The full post-ops description: the rebuilt bundle, the component tree and
 *  the refreshed parm tree (same shape the parms endpoint returns). */
export interface AircraftDesignOpsResponse {
  bundle: AircraftBuildResponse;
  geoms: AircraftDesignGeom[];
  parms: AircraftParmsResponse;
  ops_count: number;
}

/** List the project's aircraft designs. */
export async function listAircraftDesigns(projectId: string): Promise<AircraftDesignsResponse> {
  return apiClient<AircraftDesignsResponse>(`/projects/${projectId}/aircraft-designs`);
}

/** Create a design: blank (no `from`), or copied from a catalog model /
 *  fleet vsp3 / existing design. */
export async function createAircraftDesign(
  projectId: string,
  req: { name: string; from?: { model_key?: string; vsp3_path?: string; design_id?: string } },
): Promise<AircraftDesignCreated> {
  return apiClient<AircraftDesignCreated>(`/projects/${projectId}/aircraft-designs`, {
    method: "POST",
    body: req,
  });
}

/** Apply an ops batch (empty = rebuild + describe) and get the fresh bundle,
 *  geoms and parms back. Ops are persisted — every batch appends to the log. */
export async function applyAircraftDesignOps(
  projectId: string,
  designId: string,
  req: { ops: AircraftDesignOp[]; resolution?: number },
): Promise<AircraftDesignOpsResponse> {
  return apiClient<AircraftDesignOpsResponse>(
    `/projects/${projectId}/aircraft-designs/${designId}/ops`,
    { method: "POST", body: req },
  );
}

/** Export the design as a trimmed-BREP STEP (feeds the CS workbench). Slow on
 *  big models — the backend allows minutes, don't wrap this in a short timeout. */
export async function exportAircraftDesignStep(
  projectId: string,
  designId: string,
): Promise<{ step_path: string }> {
  return apiClient<{ step_path: string }>(
    `/projects/${projectId}/aircraft-designs/${designId}/export-step`,
    { method: "POST", body: {} },
  );
}

/** Delete a design (removes the file; the node is marked deleted, not erased). */
export async function deleteAircraftDesign(projectId: string, designId: string): Promise<void> {
  await apiClient<unknown>(`/projects/${projectId}/aircraft-designs/${designId}`, {
    method: "DELETE",
  });
}

// Minimal glTF typing for the subset the worker emits (embedded buffer,
// per-component meshes with POSITION + indices).
export interface GltfAccessor {
  bufferView: number;
  componentType: number;
  count: number;
  type: string;
  min?: number[];
  max?: number[];
}
export interface GltfBufferView { buffer: number; byteOffset: number; byteLength: number }
export interface GltfMesh { name?: string; primitives: { attributes: { POSITION: number }; indices: number }[] }
export interface GltfDoc {
  buffers: { byteLength: number; uri: string }[];
  bufferViews: GltfBufferView[];
  accessors: GltfAccessor[];
  meshes: GltfMesh[];
  nodes: { name?: string; mesh: number }[];
}

/** Fetch the glTF document (a full `/api/...` path) with the Thread auth token. */
export async function fetchAircraftModel(modelUrl: string): Promise<GltfDoc> {
  const token = await getAuthToken();
  const res = await fetch(modelUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Failed to load aircraft model (${res.status})`);
  return res.json();
}
